<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'petite-vue-i18n';
import { TEXT } from '../../../../../configs/text';
import { AssessmentQuestion } from '../../../../../types/assessment';
import { UserRole } from '../../../../../types/user';
import { useQuizStore, useUserStore } from '../../../../../stores';
import { ItemType, QuestionType } from '../../../../../types/assessment';

import ImageContainer from '../../../../General/ImageContainer.vue';
import VideoPlayer from '../../../../General/VideoPlayer.vue';
import AudioPlayer from '../../../../General/AudioPlayer.vue';
import { QuizAudioKey } from '../../../../../types/quiz';

const props = defineProps<{
    question: AssessmentQuestion
    showAnswer?: boolean
    layout?: 'vertical' | 'horizontal'
}>()

const { t } = useI18n();
const quizStore = useQuizStore();
const userStore = useUserStore();

const parsedDescription = computed(() => {
    if (!props.question.desc) return '';
    let question = props.question.desc;
    if (props.question.questionType === QuestionType.FILL_IN_BLANK) {
        let index = 0;
        return question.replace(/\[answer\]/g, () => {
            const choice = props.question.choices[index++];
            return `<span class="px-10 bg-white border inline-block text-violet-500 dark:text-fuchsia-500 border-violet-500 dark:border-none focus-visible:outline-none font-bold  rounded-lg text-center mb-2">
                ${props.showAnswer && userStore.userRole === UserRole.student ? choice.content.split(',').map(ans => ans.trim())[0] : index}</span>`;
        });

    }
    return question;

});


</script>
<template>
    <div class="flex h-full gap-3 overflow-hidden ">
        <div class="flex text-xl flex-col gap-3 overflow-auto bg-black/20 rounded-lg backdrop-blur-sm p-3"
            :class="{ 'flex-1': layout === 'horizontal' }">
            <h4 class="font-bold text-2xl ">
                <span class="text-yellow-500 dark:text-rose-600 uppercase">
                    {{ `${t(TEXT.question)} ${quizStore.questionIndexById(question.id) + 1}: ` }}
                </span>
                <span class="text-black dark:text-white first-letter:uppercase" v-if="question.questionDesc">
                    {{ question.questionDesc }}
                </span>
            </h4>
            <div class="flex-1 flex flex-col items-center text-center justify-center text-2xl"
                v-html="parsedDescription"></div>
            <div class="flex w-full items-center justify-center"
                v-if="question.itemType === ItemType.AUDIO && question.illustrationUrl">
                <AudioPlayer class="w-full md:w-1/2" :src="question.illustrationUrl" :audio-key="QuizAudioKey.question"
                    controls />
            </div>
        </div>

        <div class="flex flex-col justify-center items-center overflow-auto bg-black/20 rounded-lg p-3"
            :class="{ 'w-1/3': layout === 'horizontal' }"
            v-if="question.imageUrl || question.itemType !== ItemType.AUDIO && question.illustrationUrl">
            <template v-if="question.itemType !== ItemType.AUDIO && question.illustrationUrl">
                <ImageContainer imgClass="max-h-full" v-if="question.itemType === ItemType.IMAGE"
                    :key="question.illustrationUrl" :src="question.illustrationUrl" zoomable
                    class="h-full aspect-[4/3] flex-1" />
                <div v-if="question.itemType === ItemType.VIDEO"
                    class="aspect-video w-full rounded-lg overflow-hidden mb-3">
                    <VideoPlayer :key="question.illustrationUrl" :src="question.illustrationUrl" />
                </div>
            </template>
            <ImageContainer imgClass="max-h-full" v-if="question.imageUrl" :key="question.imageUrl" zoomable
                :src="question.imageUrl" class="h-full aspect-[4/3] flex-1" />
        </div>
    </div>
</template>